/**
 * Stockage des jetons de session — console web.
 *
 * Choix de sécurité (adapté du client mobile `apps/mobile/src/api/token-store.ts`,
 * sans `expo-secure-store` qui n'existe pas sur le web) :
 *
 * - **Access token en mémoire uniquement.** Jamais persisté : perdu au
 *   rechargement de l'onglet, reconstruit via un `refresh` au démarrage. Ça
 *   limite la fenêtre d'exposition en cas de XSS.
 * - **Refresh token en `localStorage`**, sous une clé namespacée. Nécessaire
 *   pour reprendre la session après un rechargement. L'API fait tourner le
 *   refresh token à chaque échange (rotation), ce qui borne sa valeur.
 *
 * Aucune donnée personnelle ici : uniquement des jetons opaques + l'e-mail
 * saisi à la connexion (propriété de l'utilisateur, local à son navigateur),
 * conservé pour afficher un libellé lisible dans l'en-tête.
 */

import { CAPACITES_ETEINTES, type Capacites } from '@silex/shared';

const NS = 'silex.dashboard';
const REFRESH_KEY = `${NS}.refreshToken`;
const IDENTITY_KEY = `${NS}.identity`;

let accessToken: string | null = null;

/**
 * Chantier « manuel vivant » (MV-SOC-01) — ce que le serveur accorde au compte
 * connecté. EN MÉMOIRE seulement, comme l'access token et pour la même raison
 * de fond : la session se rétablit par un renouvellement de jeton à chaque
 * chargement, et le serveur y relit les capacités. Les persister donnerait une
 * copie que rien ne tiendrait à jour.
 */
let capacites: Capacites = CAPACITES_ETEINTES;

/** Lecture `localStorage` tolérante (mode privé, quota, storage désactivé). */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* stockage indisponible — on continue sans persistance */
  }
}

export const tokenStore = {
  getAccessToken: (): string | null => accessToken,
  setAccessToken: (token: string | null): void => {
    accessToken = token;
  },

  getRefreshToken: (): string | null => safeGet(REFRESH_KEY),
  setRefreshToken: (token: string | null): void => safeSet(REFRESH_KEY, token),

  /** E-mail saisi à la connexion — confort d'affichage, jamais envoyé ailleurs. */
  getIdentity: (): string | null => safeGet(IDENTITY_KEY),
  setIdentity: (email: string | null): void => safeSet(IDENTITY_KEY, email),

  getCapacites: (): Capacites => capacites,
  setCapacites: (recues: Capacites): void => {
    capacites = recues;
  },

  /** Efface tout : appelé à la déconnexion et quand le refresh échoue. */
  clear: (): void => {
    accessToken = null;
    capacites = CAPACITES_ETEINTES;
    safeSet(REFRESH_KEY, null);
    safeSet(IDENTITY_KEY, null);
  },
} as const;
